import { Logger } from "../logger/browserLogger";
import { trimTrailingSlash } from "../string/replacements";

// Extend the Window interface to include ChiliPiper
declare global {
  interface Window {
    ChiliPiper?: any;
  }
}

export const showChilipiperConcierge = async (
  lead: {
    Email: string;
    FirstName?: string;
    LastName?: string;
    Company?: string;
    Country?: string;
    JobTitle?: string;
    State?: string;
    CompanySize?: string;
    Phone?: string;
  },
  chilipiperConciergeScriptUrl: string,
  logger: Logger
) => {
  const SCRIPT_ID = 'chilipiper-concierge';

  const loadScript = (): Promise<void> => {
    return new Promise((resolve, reject) => {
      if (window.ChiliPiper) return resolve();
      if (document.getElementById(SCRIPT_ID)) {
        // Wait for script to load
        const check = setInterval(() => {
          if (window.ChiliPiper) {
            clearInterval(check);
            resolve();
          }
        }, 50);
        setTimeout(() => {
          clearInterval(check);
          reject(new Error('Chilipiper script timeout'));
        }, 5000);
        return;
      }
      const script = document.createElement('script');
      script.id = SCRIPT_ID;
      script.src = chilipiperConciergeScriptUrl;
      script.crossOrigin = 'anonymous';
      script.type = 'text/javascript';
      script.onload = () => resolve();
      script.onerror = () => reject(new Error('Chilipiper script failed to load'));
      document.body.appendChild(script);
    });
  };

  try {
    await loadScript();
    if (!window.ChiliPiper) throw new Error('Chilipiper calendar failed to load');
    window.ChiliPiper.submit('emarketer', 'demo-request', {
      trigger: 'ThirdPartyForm',
      lead,
    });
  } catch (err) {
    // Log Chilipiper errors via the client-side logger for observability
    logger.warn('CHILIPIPER ERROR', { error: err instanceof Error ? err.message : String(err), lead });
  }
};

/**
 * Fetches the user's full country name via the GeoLocation Utility API.
 * If the API call succeeds, returns the 'Full Country Name' field from the response.
 * If the call fails or returns a non-OK status and returns undefined.
 *
 * @param {string} commonGeolocationUtilityUrl - The base URL for the geolocation utility API.
 * @param {Logger} logger - Logger instance for error reporting.
 * @returns {Promise<string|undefined>} Resolves to the country name or undefined on failure.
 */
export const fetchUserCountry = async (commonGeolocationUtilityUrl: string, logger: Logger): Promise<string | undefined> => {
  try {
    const getCountryUrl = trimTrailingSlash(commonGeolocationUtilityUrl) + '/getcountrybytrueclientip';
    const res = await fetch(getCountryUrl);
    if (!res.ok) throw new Error('Failed to fetch country');
    const data = await res.json();
    return data.countryName as string;
  } catch (err) {
    logger.warn('Country lookup failed, proceeding without Chilipiper');
    return undefined;
  }
};

/**
 * Schedules a demo by submitting a lead to Salesforce and Chilipiper Concierge.
 * This function triggers two asynchronous operations:
 * 1. Fire-and-forget submission to Salesforce via submitToSalesForce, with errors logged.
 * 2. Geolocation lookup to fetch the user's country, then submission to Chilipiper via showChilipiperConcierge.
 *
 * @param {object} userAccessToken - The user's access token containing email and firstName.
 * @param {string} chilipiperConciergeScriptUrl - The URL for the Chilipiper Concierge script.
 * @param {string} commonGeolocationUtilityUrl - The base URL for the geolocation utility API.
 * @param {Logger} logger - Logger instance for error reporting.
 * @returns {Promise<void>} Resolves when the Chilipiper submission completes. Salesforce submission is fire-and-forget.
 */
export const scheduleDemo = async (
  userAccessToken: { email: string; firstName?: string },
  chilipiperConciergeScriptUrl: string,
  commonGeolocationUtilityUrl: string,
  logger: Logger
): Promise<void> => {
 
  // fetch country and fire Chilipiper
  const country = await fetchUserCountry(commonGeolocationUtilityUrl, logger);

  // prevent Chilipiper from being called with an empty country, which could cause issues
  if (!country) {
    return;
  }

  await showChilipiperConcierge({
    Email: userAccessToken.email,
    FirstName: userAccessToken.firstName,
    Country: country,
  }, chilipiperConciergeScriptUrl, logger);
};
